
import map from 'lodash/map';
import generateFunnelConfigPipeline from '@/reports/funnel/funnel-aggregator-builder/funnel-aggregator-builder';
import generateGroupingPipelines from '@/reports/funnel/grouping-builder/grouping-builder';
import { VISITOR_ROW_LIMIT, MAX_CARDINALITY_LIMIT } from '@/reports/funnel/utils';
import { BuildProps } from '@/types/analytics/funnels.types';
import { operators as o, common as c } from '@pendo/aggregations';
import { MetadataTypeId } from '@/types/analytics/analytics.types';
import { PRODUCT_AREAS, SUB_PRODUCT_AREAS } from '@/utils/aggregation';
import { regionEvalExpression, regionEvalExpressionForFilter } from '@middleware/constants/group-by';

function generateWholeFunnelMetricPipeline (config: c.PipelineFunnelData): c.PipelineData[] {
    return o.pipeline(
        o.pipeline(
            o.fork(
                o.pipeline(
                    o.group(['steps'], o.groupField('count', o.count(null))),
                    o.sort(['-steps']),
                    o.accumulate({ count: 'count' }),
                    o.group(
                        [],
                        o.groupField('counts', o.list('count')),
                        o.groupField('furthestStep', {
                            max: 'steps',
                        }),
                    ),
                    o.evalExpression(
                        'percentCompleted',
                        `if(furthestStep != ${config.items.length}, 0, (counts[0]/counts[len(counts) - 1]) * 100)`,
                    ),
                    o.select({
                        percentCompleted: 'percentCompleted',
                    }),
                ),
                o.pipeline(
                    o.compute({
                        durations: {
                            diff: 'times',
                        },
                    }),
                    o.filter(`len(durations) == ${config.items.length - 1}`),
                    o.group(
                        [],
                        o.groupField('averageTimes', o.listAverage('durations')),
                        o.groupField('medianTimes', o.listMedian('durations')),
                    ),
                    o.fork(
                        o.pipeline(
                            o.unwind('averageTimes'),
                            o.group([], o.groupField('averageTimeToCompletion', o.sum('averageTimes'))),
                        ),
                        o.pipeline(
                            o.unwind('medianTimes'),
                            o.group([], o.groupField('medianTimeToCompletion', o.sum('medianTimes'))),
                        ),
                    ),
                    o.join([]),
                ),
            ),
            o.join(),
            o.evaluate('rowType', '"wholeFunnelMetrics"'),
        ),
    );
}

function generateGroupInfoPipeline (groupKeys: string[], config: c.PipelineFunnelData) {
    const percentCompletedPipeline = o.pipeline(
        o.group(['steps', ...groupKeys], o.groupField('count', o.count(null))),
        o.group(groupKeys, o.groupField('groupSteps', o.accumulateAggregator(['-steps'], { count: 'count' }))),
        o.evalExpression(
            'percentCompleted',
            `if(groupSteps[0].steps != ${config.items.length}, 0, (groupSteps[0].count/groupSteps[len(groupSteps) - 1].count) * 100)`,
        ),
    );

    const timeToCompletionPipeline = o.pipeline(
        o.compute({
            durations: {
                diff: 'times',
            },
        }),
        o.filter(`len(durations) == ${config.items.length - 1}`),
        o.group(
            groupKeys,
            o.groupField('averageTimes', o.listAverage('durations')),
            o.groupField('medianTimes', o.listMedian('durations')),
        ),
        o.fork(
            o.pipeline(
                o.unwind('averageTimes'),
                o.group(groupKeys, o.groupField('averageTimeToCompletion', o.sum('averageTimes'))),
            ),
            o.pipeline(
                o.unwind('medianTimes'),
                o.group(groupKeys, o.groupField('medianTimeToCompletion', o.sum('medianTimes'))),
            ),
        ),
    );

    const averageAndMedianPipeline = o.pipeline(
        o.compute({
            durations: {
                diff: 'times',
            },
        }),
        o.group(
            groupKeys,
            o.groupField('averageTimes', o.listAverage('durations')),
            o.groupField('medianTimes', o.listMedian('durations')),
        ),
    );

    const groupOperators = [
        o.sort(['-percentCompleted']),
        o.limit(MAX_CARDINALITY_LIMIT + 1),
        o.fork(
            o.pipeline(o.limit(MAX_CARDINALITY_LIMIT), o.evaluate('rowType', '"groupInfo"')),
            o.pipeline(
                o.reduce({
                    groupCount: o.count(null),
                }),
                o.evaluate({
                    hasExceededMaxThresholdCount: `if(groupCount > ${MAX_CARDINALITY_LIMIT}, true, false)`,
                }),
                o.evaluate('rowType', '"funnelExceedsCardinality"'),
            ),
        ),
    ];

    const ungroupedOperator = [o.evaluate('rowType', '"groupInfo"')];

    return o.pipeline(
        o.fork(percentCompletedPipeline, timeToCompletionPipeline, averageAndMedianPipeline),
        o.join(groupKeys),
        ...(groupKeys.length > 0 ? groupOperators : ungroupedOperator),
    );
}

export default {
    name: 'funnel',
    productArea: PRODUCT_AREAS.ANALYTICS,
    subProductArea: SUB_PRODUCT_AREAS.FUNNELS,
    requestId: ({ report }: { report: { id: string } }) => `funnel-${report.id}`,
    response: ({
        forceRun,
        impersonating,
        report,
    }: {
        forceRun: boolean;
        impersonating: boolean;
        report: { id: string };
    }) => {
        return {
            name: report.id,
            location: 'report',
            mimeType: 'application/json',
            report,
            forceRun,
            impersonating,
        };
    },
    build: ({ groupings, timeSeries, config }: BuildProps) => {
        const includesTrackEvents = config.items.some((item) => !!item.trackTypeId);
        const includesAgentEvents = config.items.some((item) => !!item.agentId);

        const eventClass: string[] | undefined = [];
        if (includesTrackEvents) {
            eventClass.push('track');
            eventClass.push('ui');
        }
        if (includesAgentEvents) {
            eventClass.push('agentic');
            eventClass.push('ui');
        }

        const groupingPipelines = generateGroupingPipelines(groupings);

        const { funnelAggregatorConfig, hasDevicePropFilter, bulkExpand, hasRegionFilter } = generateFunnelConfigPipeline(
            config,
            groupings,
        );
        const hasDeviceGroupByInFunnel =
            groupings?.some((group) => group.type === MetadataTypeId.Automatic) ||
            groupings?.some((group) => group.configuration?.kind === MetadataTypeId.Automatic);
        const hasDevicePropsInFunnel = hasDevicePropFilter || hasDeviceGroupByInFunnel;

        const hasRegionGroupByInFunnel = groupings?.some((group) => group.configuration?.field === 'region');

        const bulkExpandFiltersOperators = bulkExpand.map((type) => o.bulkExpand(type));

        return o.build(
            o.settings(config.blacklist),
            o.pipeline(
                o.sources.singleEvents({
                    timeSeries: { ...timeSeries },
                    appId: config.appId,
                    eventClass: includesTrackEvents || includesAgentEvents ? eventClass : undefined,
                }),
                o.identified(config.includeAnonymous || undefined),
                o.segment(config.segmentId),
                hasDevicePropsInFunnel && o.userAgent('parsedUserAgent'),
                ...bulkExpandFiltersOperators,
                hasRegionGroupByInFunnel && o.evalExpression({ country_region: regionEvalExpression }),
                hasRegionFilter && o.evalExpression({ country_region: regionEvalExpressionForFilter }),
                o.group(
                    groupingPipelines.groupingByAccountMetadata ? ['visitorId', 'accountId'] : ['visitorId'],
                    o.groupField('funnel', { funnel: { ...funnelAggregatorConfig } }),
                ),
                groupingPipelines.bulkExpand,
                o.unwind('funnel'),
                ...groupingPipelines.evals,
                o.select({
                    rowType: '"visitors"',
                    times: 'funnel.times',
                    start: 'funnel.start',
                    steps: 'funnel.steps',
                    visitorId: 'visitorId',
                    accountIds: 'funnel.accountIds',
                    recordingIds: 'funnel.recordingIds',
                    recordingSessionIds: 'funnel.recordingSessionIds',
                    tabIds: 'funnel.tabIds',
                    conversationId: 'funnel.conversationIds',
                    eventIds: 'funnel.eventIds',
                    content: 'funnel.contents',
                    ...groupingPipelines.selects,
                }),
                o.fork(
                    generateWholeFunnelMetricPipeline(config),
                    generateGroupInfoPipeline(groupingPipelines.columns, config),
                    ...map(config.items, (_item, i) => [o.filter(`steps == ${i + 1}`), o.limit(VISITOR_ROW_LIMIT)]),
                ),
            ),
        );
    },
};
